import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, LogIn, ArrowLeft, DoorOpen, LogOut, Sparkles, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/aadhaar";
import { isStudent } from "@/lib/classroom_live";
import { useLang } from "@/context/LanguageContext";
import { useRoomSync } from "@/components/breakout/useRoomSync";
import { GroupBoard } from "@/components/breakout/GroupBoard";
import { RoomChat } from "@/components/breakout/RoomChat";

const BASE = "/student/breakout/rooms";

export const RoomHeader = ({ room, onBack, onLeave }) => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="room-header">
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#007A8A]">{L(`Class ${room.class_grade} · group room`, `कक्षा ${room.class_grade} · ग्रुप रूम`)}</div>
        <div className="text-lg font-extrabold text-[#0A192F]" data-testid="room-title">{room.title}</div>
        <div className="text-sm text-slate-600" data-testid="room-topic">{L("Topic:", "विषय:")} {room.topic}</div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="room-members">
          {room.members.map((m) => <span key={m} className="px-2 py-0.5 rounded-full bg-[#E8F0FE] text-xs font-semibold">{m}</span>)}
          <span className="text-xs text-slate-500">{room.members.length}/{room.size}</span>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {onBack && <button onClick={onBack} data-testid="room-back-btn" className="h-10 px-4 rounded-full border border-slate-300 text-sm font-bold inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" />{L("All rooms", "सभी रूम")}</button>}
        {onLeave && room.status === "open" && <button onClick={onLeave} data-testid="room-leave-btn" className="h-10 px-4 rounded-full border border-[#FF3B3B]/50 text-[#C81E4B] text-sm font-bold inline-flex items-center gap-1"><LogOut className="w-4 h-4" />{L("Leave", "छोड़ें")}</button>}
      </div>
    </div>
  );
};

export const SummaryBanner = ({ room }) => {
  const { L } = useLang();
  if (room.status === "open") return null;
  return (
    <div className="rounded-3xl bg-[#0A192F] text-white p-5" data-testid="room-summary">
      <div className="font-extrabold inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#00E5FF]" />{L("Zoya Ma'am's group summary", "ज़ोया मैम का ग्रुप सारांश")}</div>
      <p className="mt-2 text-sm text-slate-200 whitespace-pre-wrap">{L(room.summary, room.summary_hi || room.summary) || L("The trainer closed this room.", "ट्रेनर ने यह रूम बंद कर दिया।")}</p>
    </div>
  );
};

const RoomView = ({ rid, me, auth, onBack }) => {
  const { L } = useLang();
  const { room, events, merge, err } = useRoomSync(BASE, rid, auth);
  const send = async (text) => {
    const { data } = await api.post(`${BASE}/${rid}/messages`, { text }, auth);
    if (data.event) merge([data.event]);
    return data;
  };
  const stroke = (s) => api.post(`${BASE}/${rid}/strokes`, s, auth).then((r) => merge([r.data]));
  const clear = () => api.post(`${BASE}/${rid}/clear`, {}, auth).then((r) => merge([r.data])).catch(() => toast.error(L("Couldn't clear the board.", "बोर्ड साफ़ नहीं हुआ।")));
  const leave = async () => { await api.post(`${BASE}/${rid}/leave`, {}, auth).catch(() => {}); onBack(); };
  if (err === 403 || err === 404) return <p className="text-sm text-[#C81E4B]" data-testid="room-error">{L("You're not in this room any more.", "आप अब इस रूम में नहीं हैं।")} <button className="underline" onClick={onBack}>{L("Back", "वापस")}</button></p>;
  if (!room) return <div className="h-64 rounded-3xl bg-white/70 animate-pulse" data-testid="room-loading" />;
  const closed = room.status !== "open";
  return (
    <div className="space-y-4" data-testid="room-view" data-status={room.status}>
      <RoomHeader room={room} onBack={onBack} onLeave={leave} />
      <SummaryBanner room={room} />
      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        <GroupBoard events={events} onStroke={stroke} onClear={clear} readOnly={closed} />
        <RoomChat events={events} me={me} closed={closed} onSend={send} />
      </div>
      {!closed && <p className="text-[11px] text-slate-500">{L("Zoya Ma'am checks every message for safety and keeps the group on topic. Your trainer can see this room.", "ज़ोया मैम हर संदेश की सुरक्षा जाँचती हैं और ग्रुप को विषय पर रखती हैं। आपके ट्रेनर यह रूम देख सकते हैं।")}</p>}
    </div>
  );
};

const RoomCard = ({ r, onOpen }) => {
  const { L } = useLang();
  const full = r.members.length >= r.size && !r.joined;
  const closed = r.status !== "open";
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-5 flex flex-col" data-testid={`room-card-${r.id}`}>
      <div className="flex items-center justify-between gap-2"><span className="font-extrabold text-[#0A192F]">{r.title}</span>
        {closed ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 inline-flex items-center gap-1"><Lock className="w-3 h-3" />{L("Closed", "बंद")}</span>
          : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{r.members.length}/{r.size}</span>}</div>
      <p className="mt-1 text-sm text-slate-600 line-clamp-2">{r.topic}</p>
      <p className="mt-1 text-xs text-slate-500 line-clamp-1">{r.members.join(", ") || L("No one yet", "अभी कोई नहीं")}</p>
      <button onClick={() => onOpen(r)} disabled={full} data-testid={`room-join-${r.id}`}
        className={`mt-4 h-10 rounded-full text-sm font-bold inline-flex items-center justify-center gap-1.5 disabled:opacity-40 ${r.joined || closed ? "border border-slate-300" : "bg-[#0A192F] text-white"}`}>
        <DoorOpen className="w-4 h-4" />{closed ? L("Read summary", "सारांश पढ़ें") : r.joined ? L("Continue", "जारी रखें") : full ? L("Full", "भरा हुआ") : L("Join", "जुड़ें")}</button>
    </div>
  );
};

const GuestRooms = () => {
  const { L } = useLang();
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-8 text-center" data-testid="rooms-guest">
      <Users className="w-10 h-10 mx-auto text-[#FF2E63]" />
      <div className="mt-3 text-xl font-extrabold text-[#0A192F]">{L("Group breakout rooms", "ग्रुप ब्रेकआउट रूम")}</div>
      <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">{L("Your trainer opens rooms for your class. Discuss with 3-5 classmates, draw on a shared whiteboard, and Zoya Ma'am sums it up.", "आपके ट्रेनर आपकी कक्षा के लिए रूम खोलते हैं। 3-5 साथियों के साथ चर्चा कीजिए, साझा व्हाइटबोर्ड पर बनाइए, और ज़ोया मैम सारांश देंगी।")}</p>
      <Link to="/student/login" className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#0A192F] text-white text-sm font-bold" data-testid="rooms-login-btn"><LogIn className="w-4 h-4" />{L("Log in as a student", "छात्र लॉगिन करें")}</Link>
    </div>
  );
};

export const StudentRooms = () => {
  const { L } = useLang();
  const student = isStudent();
  const auth = useMemo(() => authHeader("student"), []);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get(BASE, auth).then((r) => setD(r.data)).catch((e) => setD({ error: e.response?.status || "net" })), [auth]);
  useEffect(() => {
    if (!student || open) return undefined;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [student, open, load]);
  if (!student) return <GuestRooms />;
  if (open) return <RoomView rid={open} me={d?.name} auth={auth} onBack={() => setOpen(null)} />;
  if (!d) return <div className="h-48 rounded-3xl bg-white/70 animate-pulse" data-testid="rooms-loading" />;
  if (d.error) return <p className="text-sm text-[#C81E4B]" data-testid="rooms-error">{d.error === 403 ? L("Group rooms open once your admission is approved.", "एडमिशन मंज़ूर होने के बाद ग्रुप रूम खुलेंगे।") : L("Couldn't load rooms. Try again.", "रूम लोड नहीं हुए। फिर कोशिश करें।")}</p>;
  const pick = async (r) => {
    if (r.status !== "open" || r.joined) { setOpen(r.id); return; }
    try { await api.post(`${BASE}/${r.id}/join`, {}, auth); setOpen(r.id); }
    catch (e) { toast.error(e.response?.data?.detail || L("Couldn't join.", "जुड़ नहीं पाए।")); load(); }
  };
  return (
    <div data-testid="student-rooms">
      <div className="mb-4"><div className="text-xl font-extrabold text-[#0A192F] inline-flex items-center gap-2"><Users className="w-5 h-5 text-[#FF2E63]" />{L("Group rooms", "ग्रुप रूम")}</div>
        <p className="text-sm text-slate-500">{L("Rooms your trainer opened for your class. Join one, discuss and draw together.", "आपके ट्रेनर ने आपकी कक्षा के लिए जो रूम खोले हैं। किसी एक में जुड़िए, साथ में चर्चा कीजिए और बनाइए।")}</p></div>
      {d.items.length === 0 ? <p className="rounded-3xl bg-white border border-slate-200 p-6 text-sm text-slate-600" data-testid="rooms-empty">{L("No group rooms right now. Your trainer will open them during class.", "अभी कोई ग्रुप रूम नहीं है। ट्रेनर क्लास के दौरान खोलेंगे।")}</p>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{d.items.map((r) => <RoomCard key={r.id} r={r} onOpen={pick} />)}</div>}
    </div>
  );
};
